import { createContext, useContext } from 'react';
import type { WorkflowCanvasView } from './use-workflow-canvas-view';
import type {
  WorkflowDefinition,
  WorkflowStepCatalog,
  WorkflowStepProblem,
} from './utils/workflow-definition';
import type { WorkflowDefinitionEdit } from './utils/workflow-definition-edits';

/**
 * The canvas's shared state, read by step cards, connections and add-step targets by id.
 *
 * reactflow re-lays out whenever its `nodes` change, so the step data stays out of node `data`:
 * cards read it from here, and an edit re-renders the card that changed without moving anything.
 */
export interface WorkflowEditorContextValue {
  definition: WorkflowDefinition;
  catalog: WorkflowStepCatalog;
  problemsByNode: ReadonlyMap<string | null, WorkflowStepProblem[]>;
  /** A draft of the partner's own workflow. False on an active version or a platform workflow. */
  editable: boolean;
  /** One step open at a time; `null` when all are closed. */
  openNodeId: string | null;
  setOpenNodeId: (nodeId: string | null) => void;
  dispatch: (edit: WorkflowDefinitionEdit) => void;
  /** Folded outputs and the search; what the canvas shows, not what is saved. */
  view: WorkflowCanvasView;
}

const WorkflowEditorContext = createContext<WorkflowEditorContextValue | null>(null);

export const WorkflowEditorProvider = WorkflowEditorContext.Provider;

/** The editor state; throws outside `WorkflowEditorProvider`, where no canvas is mounted. */
export function useWorkflowEditor(): WorkflowEditorContextValue {
  const value = useContext(WorkflowEditorContext);
  if (!value) {
    throw new Error('useWorkflowEditor must be used inside WorkflowEditorProvider.');
  }
  return value;
}
